import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { useFarmColumns } from '../utils/columns';
import FarmsTable from '../components/farm/FarmsTable';
import { useQueryWithMsg } from '../utils/hooks';
import { getFirstQueryResult } from '../utils/graphql';
import FarmSearch from '../components/farm/FarmSearch';
import Header from '../components/layout/Header';
import Content from '../components/layout/Content';
import { useAlertContext } from '../contexts/AlertContext';
import { getTeamBillingPath } from '../utils/team';
import { searchBy } from '../utils/search';
import useCreateFarm from '../hooks/useCreateFarm';
import { FarmColumnsFragment } from '../constants/fragments';

export const TeamFarmsQuery = gql`
  ${FarmColumnsFragment}
  query TeamFarms($id: ID!) {
    team(id: $id) {
      id
      name
      hasSubscription
      hasValidSubscription
      exceedsNumPaidEditors
      allowedActions {
        id
        canEdit
        canUpdateSettings
      }
      farms {
        ...FarmColumnFields
      }
    }
  }
`;

const TeamFarmsView = () => {
  const { farmColumns } = useFarmColumns(TeamFarmsQuery);
  const filteredColumns = useMemo(
    () => farmColumns.filter(({ displayString }) => displayString !== 'Team'),
    [farmColumns],
  );
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { createFarm } = useCreateFarm({
    refetchQuery: TeamFarmsQuery,
    teamId,
  });
  const { msg, data } = useQueryWithMsg(TeamFarmsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { id: teamId },
  });
  const teamData = getFirstQueryResult(data);
  const { canEdit, canUpdateSettings } = teamData?.allowedActions || {};
  const { name: teamName, hasValidSubscription, exceedsNumPaidEditors } =
    teamData || {};

  const {
    getTeamEditorAlertDismissed,
    dismissTeamEditorAlert,
  } = useAlertContext();

  const showMembersWarning = exceedsNumPaidEditors && canUpdateSettings;

  const createButtonProps = canEdit
    ? {
        showLockedBadge: !hasValidSubscription,
        onClick: () =>
          hasValidSubscription
            ? createFarm()
            : canUpdateSettings && navigate(getTeamBillingPath(teamData)),
        lockedBadgeInfo: canUpdateSettings
          ? undefined /* Use the default string */
          : 'Invalid subscription',
        disabled: !hasValidSubscription && !canUpdateSettings,
      }
    : undefined;
  return (
    <>
      <Header>
        <FarmSearch />
      </Header>
      <Content>
        {showMembersWarning && (
          <Alert
            className="mt-4 mb-0 mx-3"
            color="danger"
            isOpen={!getTeamEditorAlertDismissed(teamData.id)}
            toggle={() => dismissTeamEditorAlert(teamData.id)}
          >
            The number of editors exceeds the available editors in your
            subscription. Some editors have been demoted to viewers until your
            subscription is updated.{' '}
            <Link to={`/app/team/${teamData.id}/members`}>
              Update&nbsp;subscription
            </Link>
          </Alert>
        )}
        <FarmsTable
          key={teamName} // NOTE: setting this key ensures that React will re-render this component when a different team is selected
          header={teamName ? `${teamName} Projects` : '...'}
          query={TeamFarmsQuery}
          cols={filteredColumns}
          sortColumn="objectUpdatedAt"
          sortDirection="desc"
          farms={teamData?.farms}
          msg={msg}
          createButtonProps={createButtonProps}
          searchable
          search={searchBy(['name'])}
          searchPlaceholder="Search team projects by name"
        />
      </Content>
    </>
  );
};

export default TeamFarmsView;
